import React, { useState, useEffect } from 'react';
import { scrapePortTracking, getActiveContainersTracking } from '../lib/api';
import { useI18n } from '../contexts/I18nContext';
import { toast } from 'sonner';
import {
  Building2, Loader2, Ship, Anchor, Package, RefreshCw,
  ExternalLink, ChevronRight, Waves, MapPin, Clock, ArrowUpRight,
} from 'lucide-react';
import { Button } from '../components/ui/button';

const TERMINALS = [
  {
    code: 'CONTECON',
    name: 'Contecon Manzanillo',
    subtitle: 'TEC II - ICTSI',
    color: '#EB4629',
    bgGradient: 'from-red-600/20 to-red-900/10',
    borderColor: 'border-red-500/40',
    textColor: 'text-red-400',
    accentBg: 'bg-red-500/15',
    has_scraper: true,
    tracking_url: 'https://www.contecon.mx/trazabilidad-de-carga',
    position: 'left',
  },
  {
    code: 'TIMSA',
    name: 'TIMSA Hutchison',
    subtitle: 'TEC I - Hutchison Ports',
    color: '#009BDE',
    bgGradient: 'from-sky-600/20 to-sky-900/10',
    borderColor: 'border-sky-500/40',
    textColor: 'text-sky-400',
    accentBg: 'bg-sky-500/15',
    has_scraper: true,
    tracking_url: 'https://smartport.com.mx/Tracing',
    position: 'center',
  },
  {
    code: 'SSA',
    name: 'SSA Mexico',
    subtitle: 'TEC I - SSA Marine',
    color: '#1B3A5C',
    bgGradient: 'from-slate-600/20 to-slate-900/10',
    borderColor: 'border-slate-400/40',
    textColor: 'text-slate-300',
    accentBg: 'bg-slate-500/15',
    has_scraper: true,
    tracking_url: 'https://www.ssamarine.mx/buscarcontenedor?id=1',
    position: 'right',
  },
];

export default function PortDashboard() {
  const { t } = useI18n();
  const [containers, setContainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState({});
  const [scanResults, setScanResults] = useState({});
  const [scanningAll, setScanningAll] = useState(false);

  useEffect(() => {
    loadContainers();
  }, []);

  const loadContainers = async () => {
    try {
      const res = await getActiveContainersTracking();
      setContainers(res.data.containers || []);
    } catch {
      toast.error(t('tracking.portLoading'));
    } finally {
      setLoading(false);
    }
  };

  const scanTerminal = async (terminalCode) => {
    const validContainers = containers.filter(
      c => c.container_number && c.container_number.length >= 10
    );
    if (validContainers.length === 0) {
      toast.warning(t('tracking.portNoContainers'));
      return;
    }

    setScanning(prev => ({ ...prev, [terminalCode]: true }));
    const results = [];
    let found = 0;

    for (const c of validContainers) {
      try {
        const res = await scrapePortTracking(c.container_number, terminalCode);
        if (res.data.success && res.data.total > 0) {
          results.push({ ...res.data, container_number: c.container_number });
          found += res.data.total;
        }
      } catch {
        // continue with next container
      }
    }

    setScanResults(prev => ({
      ...prev,
      [terminalCode]: { results, found, total_queried: validContainers.length, scanned_at: new Date() },
    }));
    setScanning(prev => ({ ...prev, [terminalCode]: false }));

    if (found > 0) {
      toast.success(`${terminalCode}: ${found} ${t('tracking.portResultsFound')}`);
    } else {
      toast.info(`${terminalCode}: ${t('tracking.portNoneFound')}`);
    }
  };

  const scanAllTerminals = async () => {
    setScanningAll(true);
    for (const term of TERMINALS.filter(tm => tm.has_scraper)) {
      await scanTerminal(term.code);
    }
    setScanningAll(false);
  };

  const totalContainers = containers.length;
  const validContainers = containers.filter(c => c.container_number?.length >= 10).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={28} className="animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="port-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <Anchor size={20} className="text-sky-400" />
            Puerto de Manzanillo
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            {totalContainers} contenedores activos ({validContainers} consultables)
          </p>
        </div>
        <Button
          onClick={scanAllTerminals}
          disabled={scanningAll || validContainers === 0}
          className="gap-2"
          size="sm"
          data-testid="scan-all-terminals-btn"
        >
          {scanningAll ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <RefreshCw size={14} />
          )}
          Consultar Todas las Terminales
        </Button>
      </div>

      {/* Port Visual Map */}
      <div className="relative">
        {/* Water background */}
        <div className="absolute inset-0 bg-gradient-to-b from-sky-950/30 via-sky-900/20 to-transparent rounded-xl -z-10" />
        <div className="absolute bottom-0 left-0 right-0 h-16 flex items-end justify-center overflow-hidden rounded-b-xl -z-10">
          <Waves size={500} className="text-sky-800/20" strokeWidth={0.5} />
        </div>

        {/* Terminal Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4">
          {TERMINALS.map((terminal) => {
            const isScanning = scanning[terminal.code];
            const result = scanResults[terminal.code];

            return (
              <TerminalCard
                key={terminal.code}
                terminal={terminal}
                isScanning={isScanning}
                result={result}
                onScan={() => scanTerminal(terminal.code)}
                containers={containers}
              />
            );
          })}
        </div>
      </div>

      {/* Results Section */}
      {Object.values(scanResults).some(r => r?.results?.length > 0) && (
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Package size={16} className="text-green-400" />
            Contenedores Encontrados en Puerto
          </h3>
          {TERMINALS.map((terminal) => {
            const result = scanResults[terminal.code];
            if (!result || result.results.length === 0) return null;
            return (
              <TerminalResults
                key={terminal.code}
                terminal={terminal}
                result={result}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}


function TerminalCard({ terminal, isScanning, result, onScan, containers }) {
  const found = result?.found || 0;
  const queried = result?.total_queried || 0;
  const scannedAt = result?.scanned_at;

  return (
    <div
      className={`relative overflow-hidden rounded-xl border ${terminal.borderColor} bg-gradient-to-br ${terminal.bgGradient} backdrop-blur-sm p-5 transition-all hover:scale-[1.01]`}
      data-testid={`terminal-card-${terminal.code}`}
    >
      {/* Terminal badge */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div
            className="w-3 h-3 rounded-full animate-pulse"
            style={{ backgroundColor: terminal.color }}
          />
          <span className={`text-xs font-bold uppercase tracking-wider ${terminal.textColor}`}>
            {terminal.code}
          </span>
        </div>
        {terminal.has_scraper ? (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-500/15 text-green-400 border border-green-500/20 font-medium">
            Automatizado
          </span>
        ) : (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/20 font-medium">
            Manual
          </span>
        )}
      </div>

      {/* Terminal name */}
      <h3 className="text-base font-bold text-foreground leading-tight">{terminal.name}</h3>
      <p className="text-xs text-muted-foreground mt-0.5">{terminal.subtitle}</p>

      {/* Stats */}
      <div className="mt-4 grid grid-cols-2 gap-2">
        <div className={`p-2.5 rounded-lg ${terminal.accentBg}`}>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Encontrados</p>
          <p className={`text-xl font-bold ${terminal.textColor}`}>{found}</p>
        </div>
        <div className={`p-2.5 rounded-lg ${terminal.accentBg}`}>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Consultados</p>
          <p className="text-xl font-bold text-foreground/70">{queried}</p>
        </div>
      </div>

      {/* Last scan */}
      {scannedAt && (
        <p className="text-[10px] text-muted-foreground mt-3 flex items-center gap-1">
          <Clock size={10} />
          {new Date(scannedAt).toLocaleString('es-MX')}
        </p>
      )}

      {/* Actions */}
      <div className="mt-4 flex gap-2">
        {terminal.has_scraper ? (
          <button
            onClick={onScan}
            disabled={isScanning}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium transition-all ${
              isScanning
                ? 'bg-muted/30 text-muted-foreground cursor-not-allowed'
                : `${terminal.accentBg} ${terminal.textColor} hover:opacity-80`
            }`}
            data-testid={`scan-terminal-${terminal.code}`}
          >
            {isScanning ? (
              <>
                <Loader2 size={12} className="animate-spin" />
                Consultando...
              </>
            ) : (
              <>
                <RefreshCw size={12} />
                Consultar
              </>
            )}
          </button>
        ) : (
          <a
            href={terminal.tracking_url}
            target="_blank"
            rel="noopener noreferrer"
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium ${terminal.accentBg} ${terminal.textColor} hover:opacity-80 transition-all`}
            data-testid={`open-terminal-${terminal.code}`}
          >
            <ExternalLink size={12} />
            Abrir Portal
          </a>
        )}
        <a
          href={terminal.tracking_url}
          target="_blank"
          rel="noopener noreferrer"
          className="p-2 rounded-lg bg-secondary/30 text-muted-foreground hover:text-foreground transition-colors"
          title="Abrir portal web"
        >
          <ArrowUpRight size={14} />
        </a>
      </div>
    </div>
  );
}


function TerminalResults({ terminal, result }) {
  return (
    <div
      className={`border ${terminal.borderColor} rounded-lg overflow-hidden`}
      data-testid={`terminal-results-${terminal.code}`}
    >
      <div className={`px-4 py-2.5 bg-gradient-to-r ${terminal.bgGradient} flex items-center gap-2`}>
        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: terminal.color }} />
        <span className={`text-xs font-bold ${terminal.textColor}`}>{terminal.name}</span>
        <span className="text-xs text-muted-foreground">
          {result.found} contenedor(es)
        </span>
      </div>

      <div className="divide-y divide-border/50">
        {result.results.map((r, idx) => (
          <div key={idx} className="px-4 py-3">
            {r.data?.map((item, i) => (
              <div key={i} className="space-y-2">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-mono text-sm font-bold text-foreground">
                    {item.contenedor || r.container_number}
                  </span>
                  {item.buque && (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Ship size={11} />
                      {item.buque}
                    </span>
                  )}
                  {item.linea_naviera && (
                    <span className="text-xs px-2 py-0.5 rounded bg-secondary/50 text-muted-foreground">
                      {item.linea_naviera}
                    </span>
                  )}
                  {item.estatus && (
                    <span className="text-xs px-2 py-0.5 rounded bg-primary/10 text-primary font-medium">
                      {item.estatus}
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  {item.bl && <div>B/L: <span className="text-foreground">{item.bl}</span></div>}
                  {item.tipo_contenedor && <div>Tipo: <span className="text-foreground">{item.tipo_contenedor}</span></div>}
                  {item.tipo_tramite && <div>Tramite: <span className="text-foreground">{item.tipo_tramite}</span></div>}
                  {item.fecha_entrada && <div>Entrada: <span className="text-foreground">{item.fecha_entrada}</span></div>}
                  {item.fecha_salida && <div>Salida: <span className="text-foreground">{item.fecha_salida}</span></div>}
                  {item.dias_en_patio && <div>Dias patio: <span className="text-foreground">{item.dias_en_patio}</span></div>}
                  {item.medio_transporte && <div>Transporte: <span className="text-foreground">{item.medio_transporte}</span></div>}
                </div>

                {/* Contecon Etapas */}
                {r.etapas && r.etapas.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {r.etapas.map((e, ei) => (
                      <div
                        key={ei}
                        className={`px-2 py-1 rounded text-[10px] border ${
                          e.estatus === 'AC' && e.fecha
                            ? 'bg-green-500/10 border-green-500/30 text-green-400'
                            : 'bg-secondary/30 border-border text-muted-foreground'
                        }`}
                      >
                        <span className="font-medium">{ei + 1}. {e.nombre}</span>
                        {e.fecha && <span className="ml-1 font-mono">{e.fecha}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
